import axios from 'axios';
import { apiRoute } from '../constants/api-constants';
import { ProvenanceSource, ResolutionState } from './residents-client';

// Typed wrapper over the parcel desk.
//
// Uses the DEFAULT axios instance on purpose, so every call inherits the
// interceptors from axios-setup.ts: withCredentials for the httpOnly session
// cookies, the X-CSRF-Token double-submit header, and the single-flight
// 401 -> refresh -> retry.

export type PackageStatus = 'awaiting_pickup' | 'picked_up' | 'returned' | 'void';

export interface Package {
  id: string;
  building_id: number;
  unit_id: number | null;
  unit_label: string;
  carrier: string;
  // Only the LAST FOUR characters of the tracking number are readable. The
  // full value is a carrier-side lookup key for someone else's parcel, so it is
  // encrypted at rest and never returned in a list.
  tracking_last4: string;
  recipient_name: string;
  parcel_count: number;
  shelf: string;
  status: PackageStatus;
  logged_at: string;
  logged_by: string | null;
  picked_up_at: string | null;
  picked_up_by: string | null;
  released_to: string;
  notes: string;
  log_source: string | null;
  log_source_id: number | null;
  field_provenance: Record<
    string,
    { source: ProvenanceSource; raw?: string; confidence?: number }
  >;
  recipient_occupancy_id: string | null;
  recipient_resolution: ResolutionState;
  released_to_occupancy_id: string | null;
  released_to_resolution: ResolutionState;
  // Derived server-side so the FE never reimplements "can this be handed over
  // yet". False while the recipient is unresolved — the parcel-to-the-wrong-
  // Maria guard.
  can_release: boolean;
}

export interface PackageListResponse {
  building_id: number;
  count: number;
  packages: Package[];
}

export interface LogPackageInput {
  recipient_name: string;
  unit_id?: number | null;
  unit_label?: string;
  carrier?: string;
  tracking?: string;
  parcel_count?: number;
  shelf?: string;
  notes?: string;
  // Omit both to let autocapture decide; send them when a human picked.
  recipient_occupancy_id?: string | null;
  recipient_resolution?: ResolutionState;
  field_provenance?: Record<
    string,
    { source: ProvenanceSource; raw?: string; confidence?: number }
  >;
}

export interface ReleasePackageInput {
  // WHO ACTUALLY COLLECTED IT — not always the recipient. A spouse collects, a
  // neighbour collects, and this is the field that answers the question in a
  // dispute.
  released_to: string;
  released_to_occupancy_id?: string | null;
  released_to_resolution?: ResolutionState;
}

export const listPackages = async (
  buildingId: number,
  opts: { status?: PackageStatus | 'all'; unresolved?: boolean } = {}
): Promise<PackageListResponse> => {
  const params: Record<string, string> = {};
  if (opts.status) params.status = opts.status;
  if (opts.unresolved) params.unresolved = 'true';

  const res = await axios.get<PackageListResponse>(
    `${apiRoute}/buildings/${buildingId}/packages`,
    { params }
  );

  return res.data;
};

export const logPackage = async (
  buildingId: number,
  input: LogPackageInput
): Promise<Package> => {
  const res = await axios.post<Package>(
    `${apiRoute}/buildings/${buildingId}/packages`,
    input
  );

  return res.data;
};

/** ONE parcel by id — for surfaces that know the parcel but not its building
 *  (the record modal's "take it out"). Scoped server-side like every other
 *  /packages/:id route; another organisation's id is a 404. */
export const getPackage = async (packageId: string): Promise<Package> => {
  const res = await axios.get<Package>(`${apiRoute}/packages/${packageId}`);

  return res.data;
};

export const releasePackage = async (
  packageId: string,
  input: ReleasePackageInput
): Promise<Package> => {
  const res = await axios.post<Package>(
    `${apiRoute}/packages/${packageId}/release`,
    input
  );

  return res.data;
};

export const closePackage = async (
  packageId: string,
  status: 'returned' | 'void',
  notes?: string
): Promise<Package> => {
  const res = await axios.post<Package>(`${apiRoute}/packages/${packageId}/close`, {
    status,
    notes,
  });

  return res.data;
};
